import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { Btn, ConfirmDialog, TextInput } from "./atoms";

export default function MediaPage() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const fileRef = useRef(null);

  const refresh = () => api.mediaList().then(setItems);
  useEffect(() => { refresh(); }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      await api.mediaUpload(f);
      await refresh();
    } catch (err) {
      alert(err.message || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setAlt = async (id, alt) => {
    await api.mediaUpdate(id, { alt });
    setItems((items || []).map((m) => (m.id === id ? { ...m, alt } : m)));
  };

  const onDelete = async () => {
    await api.mediaDelete(confirmDel.id);
    setConfirmDel(null);
    refresh();
  };

  const fullUrl = (url) =>
    url.startsWith("http") ? url : `${api.base}${url}`;

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(fullUrl(url));
  };

  return (
    <div data-testid="admin-media" className="pb-20">
      <div className="flex items-center justify-between mb-9">
        <div>
          <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">Media</div>
          <h1 className="mt-3 font-display text-3xl font-black tracking-[-0.025em]">Library</h1>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*"
            onChange={onUpload}
            className="hidden"
            data-testid="media-upload-input"
          />
          <Btn onClick={() => fileRef.current?.click()} disabled={busy} data-testid="media-upload-btn">
            {busy ? "Uploading…" : "＋ Upload"}
          </Btn>
        </div>
      </div>

      {items === null ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-neutral-500">No media yet. Upload your first asset.</div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((m) => (
            <li key={m.id} className="bg-[#0F141A] border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="relative aspect-[4/3] bg-black/40">
                {m.content_type?.startsWith("image/") ? (
                  <img src={fullUrl(m.url)} alt={m.alt || ""} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <video src={fullUrl(m.url)} className="absolute inset-0 w-full h-full object-cover" muted />
                )}
              </div>
              <div className="p-4 space-y-2">
                <div className="text-[0.7rem] text-neutral-500 truncate font-mono">{m.filename}</div>
                <TextInput
                  value={m.alt}
                  onChange={(v) => setAlt(m.id, v)}
                  placeholder="Alt text"
                />
                <div className="flex items-center gap-2">
                  <Btn variant="ghost" onClick={() => copyUrl(m.url)} className="!text-[0.6rem]">Copy URL</Btn>
                  <Btn variant="danger" onClick={() => setConfirmDel(m)} className="!text-[0.6rem]">Delete</Btn>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        title="Delete this media file?"
        message={`This will permanently remove "${confirmDel?.filename}". Cannot be undone.`}
        onConfirm={onDelete}
        onCancel={() => setConfirmDel(null)}
        danger
      />
    </div>
  );
}
